import { fetchUserProfile } from './authService';
import { formatAbbreviatedXp } from '../utils/formatAbbreviatedXp';

/** Wallet tab → Bazaar top/store tab for Buy More / Redeem navigation */
export const WALLET_BAZAAR_TABS = {
  xps: { topTab: 'mart' },
  rapcoins: { topTab: 'store', storeTab: 'rapcoin' },
  rapgems: { topTab: 'store', storeTab: 'rapgem' },
  viproom: { topTab: 'store', storeTab: 'vip-room' },
};

export const WALLET_CURRENCIES = {
  xps: {
    id: 'xps',
    label: 'XPs',
    balanceLabel: 'Available XPs',
    actionLabel: 'Redeem XPs',
    cardVariant: 'gold',
    /** Figma 11051:14118 */
    description:
      'XP (Experience Points) is a reward you earn by participating in a game. It tracks your progress and skill level, helping you unlock more XP.',
  },
  rapcoins: {
    id: 'rapcoins',
    label: 'RapCoins',
    balanceLabel: 'Available RapCoins',
    actionLabel: 'Buy More',
    cardVariant: 'orange',
    /** Figma 11051:14180 */
    description:
      'RapCoins are an in-app virtual currency that let you unlock entry to play the games on Rapchik. They are designed to enhance your experience by giving you more ways to interact, earn rewards and participate within the platform.',
  },
  rapgems: {
    id: 'rapgems',
    label: 'RapGems',
    balanceLabel: 'Available RapGems',
    actionLabel: 'Buy More',
    cardVariant: 'pink',
    /** Figma 11051:14993 */
    description:
      'RapGems are a premium in-app currency used to unlock your moves and elevate your experience. They are more valuable than regular rewards and are typically used for special actions like boosts and swaps within the platform.',
  },
  viproom: {
    id: 'viproom',
    label: 'VIP ROOM',
    balanceLabel: 'Available VIP Rooms',
    actionLabel: 'Buy More',
    cardVariant: 'dark',
    /** Figma 11059:1034 */
    description:
      'VIP Rooms are tokens that help you create a private space to have fun with family and friends privately. There are 3 types of rooms: Family, Kitty Party, and Corporate.',
  },
};

function pickString(item, keys, fallback = '') {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== '') return String(value).trim();
  }
  return fallback;
}

function pickNumber(item, keys, fallback = 0) {
  for (const key of keys) {
    const value = item?.[key];
    if (value != null && value !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return fallback;
}

function getEmptyWalletData() {
  return {
    balances: {
      xps: 0,
      rapcoins: 0,
      rapgems: 0,
      viproom: 0,
    },
    roomCounts: { family: 0, kitty: 0, corporate: 0 },
    warningMessage: '',
  };
}

function unwrapProfileData(profile) {
  if (!profile || typeof profile !== 'object') return null;
  return profile.data && typeof profile.data === 'object' ? profile.data : profile;
}

function normalizeProfileWalletData(profile) {
  const data = unwrapProfileData(profile);
  if (!data) return null;

  const roomBreakup = data.roomBreakup ?? data.roomBreakUp ?? data.roomCounts ?? {};
  const hasAnyBalance = ['totalXP', 'totalXp', 'totalCoins', 'totalGems', 'vipRoom'].some(
    (key) => data?.[key] != null
  );
  if (!hasAnyBalance && !Object.keys(roomBreakup).length) return null;

  return {
    balances: {
      xps: pickNumber(data, ['totalXP', 'totalXp', 'xp']),
      rapcoins: pickNumber(data, ['totalCoins', 'rapCoins', 'coins']),
      rapgems: pickNumber(data, ['totalGems', 'rapGems', 'gems']),
      viproom: pickNumber(data, ['vipRoom', 'viproom', 'vip']),
    },
    warningMessage: pickString(data, ['warningMessage', 'lapseMessage'], ''),
    roomCounts: {
      family: pickNumber(roomBreakup, ['familyRoom', 'family']),
      kitty: pickNumber(roomBreakup, ['kittyRoom', 'kitty', 'party', 'kittyParty']),
      corporate: pickNumber(roomBreakup, ['corporateRoom', 'corporate']),
    },
  };
}

export async function fetchWalletData() {
  let data = null;

  try {
    const profile = await fetchUserProfile();
    data = normalizeProfileWalletData(profile);
  } catch {
    data = null;
  }

  if (!data) data = getEmptyWalletData();

  return {
    balances: data.balances,
    warningMessage: data.warningMessage,
    roomCounts: data.roomCounts ?? {},
  };
}

export function formatWalletBalance(value) {
  return formatAbbreviatedXp(value);
}
